/**
 * The strip that tells you which box you're on (#1052).
 *
 * The sandbox is a deliberate look-alike of production — same code, same
 * chrome — which is exactly what makes it useful and exactly what makes it
 * dangerous. Two tabs both titled "Groundwork Command", one of them the live
 * business. The runbook has long claimed an amber SANDBOX pill, but that pill
 * only ever existed in the legacy dev probe shell; the React app people
 * actually use had no environment marker at all.
 *
 * Pinned ABOVE the top bar rather than inside the scrolling main, because a
 * marker you can scroll out of frame is a marker you stop seeing.
 */
import { FlaskConical } from "lucide-react";

import { environmentNotice } from "@/lib/environment-notice";
import { useHealthQuery } from "@/lib/health-query";
import { cn } from "@/lib/utils";

export function EnvironmentBanner({ className }: { className?: string }) {
    // Shared with ApiStatusBanner — one cached, bounded /health poll rather
    // than two components racing to define the same cache key.
    const health = useHealthQuery();

    const notice = environmentNotice(health.data);
    // Production and an unreadable /health both render nothing. A false alarm
    // on the live business would train people to ignore the banner.
    if (!notice) return null;

    return (
        <div
            role="status"
            aria-live="polite"
            data-testid="environment-banner"
            className={cn(
                "flex shrink-0 items-center justify-center gap-2 bg-amber-500 px-3 py-1.5 text-center text-amber-950",
                className,
            )}
        >
            <FlaskConical className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="text-xs font-bold tracking-wide sm:text-sm">{notice.label}</span>
            <span className="text-xs sm:text-sm">{notice.detail}</span>
        </div>
    );
}
